import React, { useCallback, useContext, useEffect, useState } from 'react';
import { makeSafeRequest } from '../../../common/makeSafeRequest';
import Preloader from '../../../components/common/Preloader';
import { AppInfoContext } from '../../../components/contexts/AppInfoContext';
import SystemPage from '../../../components/system-page/SystemPage';
import { Archetype, ListForcePackageData, Optional } from '../../../types/common';
import { FiltersData, InputData, PlatformData, PlatformSubtype } from '../../../types/systems';
import { defaultSettings, platformsSettingsTemplate } from './defaultSettings';

const Platforms: React.FC = () => {
	const [filters, setFilters] = useState<FiltersData>();
	const [inputData, setInputData] = useState<InputData>();
	const { currentPid } = useContext(AppInfoContext);

	const initialize = useCallback(
		async (pid: number, fp: number | string, side: number | string) => {
			const filters = await downloadFilters(pid, fp, side);
			const inpData = await downloadInputData();

			if (!filters || !inpData) {
				return false;
			}

			setFilters(filters);
			setInputData(inpData);
			return true;
		},
		[]
	);

	const downloadFilters = async (pid: number, fp: number | string, side: number | string) => {
		return await makeSafeRequest<FiltersData>('platforms/', 'GET', {
			params: { pid: pid, fp: fp, side: side },
		});
	};

	const downloadSystemSettings = async (systemId: number) => {
		return await makeSafeRequest<PlatformData>(`platforms/${systemId}/`, 'GET');
	};

	const downloadInputData = async () => {
		return await makeSafeRequest<InputData>('platforms/input-data/', 'GET');
	};

	const addPlatform = async (settings: Optional<PlatformData, 'id'>) => {
		return await makeSafeRequest<PlatformData>('platforms/', 'POST', {
			payload: settings,
			onSuccessMessage: 'Platform added successfully!',
		});
	};

	const deletePlatform = async (settingsId: number) => {
		return await makeSafeRequest<null>(`platforms/${settingsId}/`, 'DELETE', {
			onSuccessMessage: 'Platform deleted successfully!',
		});
	};

	const savePlatform = async (settings: Optional<PlatformData, 'id'>) => {
		return await makeSafeRequest<PlatformData>(`platforms/${settings.id}/`, 'PUT', {
			payload: settings,
			onSuccessMessage: 'Platform saved successfully!',
		});
	};

	const duplicatePlatform = async (settingsId: number) => {
		return await makeSafeRequest<PlatformData>(`platforms/${settingsId}/duplicate/`, 'POST', {
			onSuccessMessage: 'Platform duplicated successfully!',
		});
	};

	useEffect(() => {
		if (inputData && inputData.archetypes && platformsSettingsTemplate) {
			platformsSettingsTemplate.sections['Categories'].mobility = {
				type: 'select',
				data: (inputData.archetypes as Archetype[]).map((archetype: Archetype) => {
					return {
						value: archetype.id.toString(),
						label: archetype.name || archetype.id.toString(),
					};
				}),
			};
			platformsSettingsTemplate.sections['Categories'].subtype = {
				type: 'select',
				data: [
					{ value: '0', label: 'NONE' },
					...(inputData.platform_subtypes as { id: number; name: string }[]).map(
						(subtype: PlatformSubtype) => {
							return { value: subtype.id.toString(), label: subtype.name };
						}
					),
				],
			};
		}
	}, [inputData]);

	useEffect(() => {
		initialize(currentPid, '', '');
	}, [initialize, currentPid]);

	return filters && inputData ? (
		<SystemPage
			name="platform"
			forcePackageData={filters.fps as ListForcePackageData[]}
			systemsData={filters.platforms as PlatformData[]}
			inputData={inputData}
			initializeFilters={initialize}
			downloadSystemSettings={downloadSystemSettings}
			systemSettingsTemplate={platformsSettingsTemplate}
			defaultSettings={defaultSettings}
			addSystem={addPlatform}
			saveSystem={savePlatform}
			deleteSystem={deletePlatform}
			duplicateSystem={duplicatePlatform}
		></SystemPage>
	) : (
		<Preloader></Preloader>
	);
};

export default Platforms;
